import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { setBotActiveDeck, updateBotDeck } from '../../api/admin';
import { Pill } from '../../components/ui';
import { DECK_SIZE, type BotDeck, type OwnedCard } from '../../types/admin';

/**
 * 12-slot deck editor.
 *
 * The client-side rules (exactly DECK_SIZE distinct owned instances) mirror the
 * server's validateDeckInstances purely for instant feedback — the server is
 * still the authority, and its INVALID_DECK / NOT_OWNED errors are surfaced
 * verbatim if anything slips through.
 */
export function BotDeckEditor({
  botId,
  decks,
  cards,
  onChanged,
}: {
  botId: string;
  decks: BotDeck[];
  cards: OwnedCard[];
  onChanged: () => void;
}): React.JSX.Element {
  const [selectedDeckId, setSelectedDeckId] = useState<string>(decks[0]?.deckId ?? '');
  const deck = useMemo(
    () => decks.find((d) => d.deckId === selectedDeckId) ?? decks[0],
    [decks, selectedDeckId],
  );

  const [name, setName] = useState(deck?.name ?? '');
  const [chosen, setChosen] = useState<string[]>(deck?.instanceIds ?? []);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Re-seed the editor from server truth whenever the deck data changes —
  // including the refetch triggered by our own save.
  useEffect(() => {
    setName(deck?.name ?? '');
    setChosen(deck?.instanceIds ?? []);
    setError(null);
  }, [deck?.deckId, deck?.name, deck?.instanceIds]);

  // Clearing the status message is deliberately NOT part of the effect above:
  // saving triggers a refetch, which would immediately wipe the "Deck salvo."
  // confirmation the operator needs to see. Only switching decks clears it.
  useEffect(() => {
    setStatus(null);
  }, [deck?.deckId]);

  const byInstance = useMemo(() => new Map(cards.map((c) => [c.instanceId, c])), [cards]);
  const chosenSet = useMemo(() => new Set(chosen), [chosen]);
  const pool = useMemo(
    () => cards.filter((c) => !chosenSet.has(c.instanceId)),
    [cards, chosenSet],
  );

  if (!deck) {
    return (
      <section className="panel">
        <h2 className="panel__title">Deck</h2>
        <div className="empty-state">Este bot não tem decks.</div>
      </section>
    );
  }

  const add = (instanceId: string): void => {
    if (chosen.length >= DECK_SIZE || chosenSet.has(instanceId)) return;
    setChosen((prev) => [...prev, instanceId]);
    setStatus(null);
  };
  const remove = (instanceId: string): void => {
    setChosen((prev) => prev.filter((id) => id !== instanceId));
    setStatus(null);
  };

  /**
   * Mirrors the server's validateDeckInstances: exactly DECK_SIZE slots, all
   * distinct, all owned by this bot.
   *
   * Checking only the count is not enough — a deck loaded with a repeated or
   * no-longer-owned instance has 12 entries yet is still rejected upstream
   * (DUPLICATE_INSTANCE / NOT_OWNED). Offering a Save that is guaranteed to
   * fail is worse than disabling it with a reason.
   */
  const validation = useMemo((): { ok: boolean; reason?: string } => {
    if (chosen.length !== DECK_SIZE) {
      const diff = DECK_SIZE - chosen.length;
      return {
        ok: false,
        reason:
          diff > 0
            ? `faltam ${diff} carta(s) — um deck precisa de exatamente ${DECK_SIZE}`
            : `há ${-diff} carta(s) a mais — um deck precisa de exatamente ${DECK_SIZE}`,
      };
    }
    if (new Set(chosen).size !== chosen.length) {
      return { ok: false, reason: 'há cartas repetidas — cada carta só pode ocupar um espaço' };
    }
    const missing = chosen.filter((id) => !byInstance.has(id));
    if (missing.length > 0) {
      return {
        ok: false,
        reason: `${missing.length} carta(s) não pertencem mais a este bot`,
      };
    }
    return { ok: true };
  }, [chosen, byInstance]);

  const complete = validation.ok;
  const dirty =
    name !== deck.name || chosen.join(',') !== deck.instanceIds.join(',');

  const save = async (): Promise<void> => {
    // Defensive: the button is disabled in this state, but never send a deck
    // the rules already reject.
    if (!validation.ok) {
      setError(`Deck inválido: ${validation.reason}.`);
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await updateBotDeck(botId, deck.deckId, name.trim() || deck.name, chosen);
      setStatus('Deck salvo.');
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar o deck.');
    } finally {
      setBusy(false);
    }
  };

  const makeActive = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await setBotActiveDeck(botId, deck.deckId);
      setStatus('Deck definido como ativo.');
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível ativar o deck.');
    } finally {
      setBusy(false);
    }
  };

  // Render at least DECK_SIZE slots, but never fewer than the deck actually
  // holds: a deck that arrived with extra instances would otherwise hide them,
  // leaving them unremovable and the deck permanently unsaveable.
  const slotCount = Math.max(DECK_SIZE, chosen.length);
  const slots = Array.from({ length: slotCount }, (_, i) => chosen[i] ?? null);

  return (
    <section className="panel">
      <header className="panel__header">
        <h2 className="panel__title">Deck</h2>
        <div className="panel__actions">
          {decks.length > 1 ? (
            <select
              value={deck.deckId}
              onChange={(e) => setSelectedDeckId(e.target.value)}
              disabled={busy}
            >
              {decks.map((d) => (
                <option key={d.deckId} value={d.deckId}>
                  {d.name}
                  {d.isActive ? ' (ativo)' : ''}
                </option>
              ))}
            </select>
          ) : null}
          {deck.isActive ? (
            <Pill tone="success">ativo</Pill>
          ) : (
            <button type="button" className="btn btn--sm" onClick={() => void makeActive()} disabled={busy}>
              Tornar ativo
            </button>
          )}
        </div>
      </header>

      <label className="field">
        <span>Nome do deck</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} />
      </label>

      <div className="deck-status">
        <strong className={complete ? 'deck-status--ok' : 'deck-status--warn'}>
          {chosen.length}/{DECK_SIZE}
        </strong>{' '}
        cartas selecionadas
        {validation.reason ? (
          <span className="deck-status--warn"> — {validation.reason}.</span>
        ) : null}
      </div>

      <div className="slot-grid">
        {slots.map((instanceId, index) => {
          const card = instanceId ? byInstance.get(instanceId) : undefined;
          return (
            <div
              className={`slot${instanceId ? ' slot--filled' : ''}`}
              key={instanceId ?? `empty-${index}`}
              onClick={() => (instanceId ? remove(instanceId) : undefined)}
              title={instanceId ? 'Clique para remover do deck' : 'Vazio'}
            >
              {card?.artUrl ? (
                <img className="slot__art" src={card.artUrl} alt="" loading="lazy" />
              ) : null}
              <span className="slot__label">
                {card ? (card.name ?? card.cardId) : index + 1}
              </span>
              {/* An instance pinned by the deck but no longer owned: the deck is
                  broken and will fail to load in a match until it is fixed. */}
              {instanceId && !card ? <Pill tone="danger">carta ausente</Pill> : null}
            </div>
          );
        })}
      </div>

      <h3 className="panel__subtitle">Disponíveis ({pool.length})</h3>
      <div className="pool-grid">
        {pool.map((card) => (
          <button
            type="button"
            key={card.instanceId}
            className="pool-tile"
            disabled={chosen.length >= DECK_SIZE}
            onClick={() => add(card.instanceId)}
            title={
              chosen.length >= DECK_SIZE ? 'O deck já está completo' : 'Adicionar ao deck'
            }
          >
            {card.artUrl ? (
              <img className="pool-tile__art" src={card.artUrl} alt="" loading="lazy" />
            ) : (
              <span className="pool-tile__art pool-tile__art--empty" />
            )}
            <span className="pool-tile__name">{card.name ?? card.cardId}</span>
          </button>
        ))}
        {pool.length === 0 ? (
          <div className="empty-state">Todas as cartas estão no deck.</div>
        ) : null}
      </div>

      {error ? <div className="form-error">{error}</div> : null}
      {status ? <div className="form-ok">{status}</div> : null}

      <button
        type="button"
        className="btn btn--primary"
        disabled={!complete || !dirty || busy}
        // Spell out why it is blocked; a silently greyed-out button just looks
        // broken. The server enforces the same rule regardless.
        title={
          !complete
            ? `Deck inválido: ${validation.reason}`
            : !dirty
              ? 'Nenhuma alteração para salvar'
              : 'Salvar deck'
        }
        onClick={() => void save()}
      >
        {busy ? 'Salvando…' : 'Salvar deck'}
      </button>
    </section>
  );
}
